// One CSV/print cell. A blank cell is the empty string rather than null, so the
// row serializer never has to special-case a missing value; numeric columns stay
// numbers so a spreadsheet keeps treating them as numeric.
export type ExportCellValue = string | number;

// A column definition as rowsToCsv/printRows consume it. Generic over the row so
// each builder below declares exactly which row shape its accessors assume.
export type ExportColumn<Row> = {
  header: string;
  value: (row: Row) => ExportCellValue;
};

// A date column's value straight off the row: an ISO date string, or nothing.
export type ExportDateValue = string | null | undefined;

// The caller's date formatter (src/lib/dateUtils fmt). Optional at every call
// site — an unformatted ISO date is the documented fallback.
export type ExportDateFormatter = (value: string) => string;

// Anything Number() is asked to coerce here: a numeric column, a numeric string
// from a text column, or a null/absent value that renders blank.
export type ExportNumericValue = number | string | null | undefined;

function number(value: ExportNumericValue): ExportCellValue {
  const n = Number(value);
  return Number.isFinite(n) ? n : '';
}

function rounded(value: ExportNumericValue, digits = 1): ExportCellValue {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return Math.round(n * 10 ** digits) / 10 ** digits;
}

function money(value: ExportNumericValue): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return '$' + n.toFixed(2);
}

function dateValue(fmt: ExportDateFormatter | undefined, value: ExportDateValue): string {
  if (!value) return '';
  return typeof fmt === 'function' ? fmt(value) : value;
}

// The Activity Log row, as the export reads it. `deleted_at` is what masks a
// removed comment body; `entity_id` is the fallback label for a row whose
// entity has no display name.
export type ActivityLogExportRow = {
  created_at?: string | null;
  actor_display_name?: string | null;
  event_type?: string | null;
  entity_type?: string | null;
  entity_label?: string | null;
  entity_id?: string | null;
  body?: string | null;
  deleted_at?: string | null;
  mentioned_profile_names?: Array<string | null> | null;
  id?: string | null;
};

export type ActivityLogExportOptions = {
  entityTypeLabels?: Record<string, string>;
  eventTypeLabels?: Record<string, string>;
};

export function buildActivityLogExportColumns({
  entityTypeLabels = {},
  eventTypeLabels = {},
}: ActivityLogExportOptions = {}): ExportColumn<ActivityLogExportRow>[] {
  return [
    {header: 'Created at', value: (r) => r.created_at || ''},
    {header: 'Actor', value: (r) => r.actor_display_name || ''},
    // The two label lookups are asserted to a string key because a null or
    // undefined event/entity type is an intended MISS, not an error: JavaScript
    // coerces the key to 'null'/'undefined', neither map holds those, and the
    // `|| r.event_type || ''` fallback then renders the raw value or a blank.
    // The assertion states that; it does not alter the lookup.
    {header: 'Event', value: (r) => eventTypeLabels[r.event_type as string] || r.event_type || ''},
    {header: 'Entity type', value: (r) => entityTypeLabels[r.entity_type as string] || r.entity_type || ''},
    {header: 'Entity label', value: (r) => r.entity_label || r.entity_id || ''},
    {header: 'Body', value: (r) => (r.deleted_at ? '(comment deleted)' : r.body || '')},
    {
      header: 'Mentions',
      value: (r) =>
        Array.isArray(r.mentioned_profile_names) ? r.mentioned_profile_names.filter(Boolean).join(', ') : '',
    },
    {header: 'Record ID', value: (r) => r.id || ''},
  ];
}

export type EquipmentFleetExportRow = {
  name?: string | null;
  category?: string | null;
  status?: string | null;
  fuel_type?: string | null;
  tracking_unit?: string | null;
  current_reading?: ExportNumericValue;
  last_fueling_date?: ExportDateValue;
  serial_number?: string | null;
  id?: string | number | null;
};

export type EquipmentFleetExportOptions = {
  fmt?: ExportDateFormatter;
  // Hours vs miles vs none — the reading's unit governs how it renders, so the
  // caller's formatter takes both.
  fmtReading?: (reading: ExportNumericValue, unit: string | null | undefined) => ExportCellValue;
};

export function buildEquipmentFleetExportColumns({
  fmt,
  fmtReading,
}: EquipmentFleetExportOptions = {}): ExportColumn<EquipmentFleetExportRow>[] {
  return [
    {header: 'Equipment name', value: (r) => r.name || ''},
    {header: 'Category', value: (r) => r.category || ''},
    {header: 'Status', value: (r) => r.status || ''},
    {header: 'Fuel type', value: (r) => r.fuel_type || ''},
    {header: 'Tracking unit', value: (r) => r.tracking_unit || ''},
    {
      header: 'Current reading',
      value: (r) =>
        typeof fmtReading === 'function'
          ? fmtReading(r.current_reading, r.tracking_unit)
          : r.current_reading != null
            ? r.current_reading
            : '',
    },
    {header: 'Last fueling date', value: (r) => dateValue(fmt, r.last_fueling_date)},
    {header: 'Serial number', value: (r) => r.serial_number || ''},
    {header: 'Record ID', value: (r) => r.id || ''},
  ];
}

// Shared by the cattle and sheep processing-batch exports — hence animalLabel.
export type ProcessingBatchExportRow = {
  name?: string | null;
  status?: string | null;
  planned_process_date?: ExportDateValue;
  actual_process_date?: ExportDateValue;
  animal_count?: ExportNumericValue;
  total_live_weight?: ExportNumericValue;
  total_hanging_weight?: ExportNumericValue;
  yield_pct?: ExportNumericValue;
  notes?: string | null;
  id?: string | null;
};

export type ProcessingBatchExportOptions = {
  fmt?: ExportDateFormatter;
  animalLabel?: string;
};

export function buildProcessingBatchExportColumns({
  fmt,
  animalLabel = 'Animal',
}: ProcessingBatchExportOptions = {}): ExportColumn<ProcessingBatchExportRow>[] {
  return [
    {header: 'Batch', value: (r) => r.name || ''},
    {header: 'Status', value: (r) => r.status || ''},
    {header: 'Planned process date', value: (r) => dateValue(fmt, r.planned_process_date)},
    {header: 'Actual process date', value: (r) => dateValue(fmt, r.actual_process_date)},
    {header: animalLabel + ' count', value: (r) => number(r.animal_count)},
    {header: 'Total live weight', value: (r) => rounded(r.total_live_weight, 1)},
    {header: 'Total hanging weight', value: (r) => rounded(r.total_hanging_weight, 1)},
    {header: 'Yield %', value: (r) => rounded(r.yield_pct, 1)},
    {header: 'Notes', value: (r) => r.notes || ''},
    {header: 'Record ID', value: (r) => r.id || ''},
  ];
}

// The `export_*` fields are computed by the list view's daily-report metric
// helper before the row reaches here, not columns on the stored batch.
export type BroilerBatchExportRow = {
  name?: string | null;
  export_status?: string | null;
  status?: string | null;
  breed?: string | null;
  hatchery?: string | null;
  hatchDate?: ExportDateValue;
  processingDate?: ExportDateValue;
  time_on_farm?: string | null;
  schooner?: string | null;
  birdCount?: ExportNumericValue;
  birdCountActual?: ExportNumericValue;
  totalToProcessor?: ExportNumericValue;
  export_mortality?: ExportNumericValue;
  export_starter_lbs?: ExportNumericValue;
  export_grower_lbs?: ExportNumericValue;
  export_total_feed_lbs?: ExportNumericValue;
  export_feed_per_processed_bird?: ExportNumericValue;
  avgDressedLbs?: ExportNumericValue;
  id?: string | null;
};

export type DateOnlyExportOptions = {
  fmt?: ExportDateFormatter;
};

export function buildBroilerBatchExportColumns({
  fmt,
}: DateOnlyExportOptions = {}): ExportColumn<BroilerBatchExportRow>[] {
  return [
    {header: 'Batch', value: (r) => r.name || ''},
    {header: 'Status', value: (r) => r.export_status || r.status || ''},
    {header: 'Breed', value: (r) => r.breed || ''},
    {header: 'Hatchery', value: (r) => r.hatchery || ''},
    {header: 'Hatch date', value: (r) => dateValue(fmt, r.hatchDate)},
    {header: 'Processing date', value: (r) => dateValue(fmt, r.processingDate)},
    {header: 'Time on farm', value: (r) => r.time_on_farm || ''},
    {header: 'Schooner', value: (r) => r.schooner || ''},
    {header: 'Birds ordered', value: (r) => number(r.birdCount)},
    {header: 'Birds arrived', value: (r) => number(r.birdCountActual)},
    {header: 'To processor', value: (r) => number(r.totalToProcessor)},
    {header: 'Mortality', value: (r) => number(r.export_mortality)},
    {header: 'Starter feed lbs', value: (r) => rounded(r.export_starter_lbs, 1)},
    {header: 'Grower feed lbs', value: (r) => rounded(r.export_grower_lbs, 1)},
    {header: 'Total feed lbs', value: (r) => rounded(r.export_total_feed_lbs, 1)},
    {header: 'Feed per processed bird', value: (r) => rounded(r.export_feed_per_processed_bird, 2)},
    {header: 'Avg dressed lbs', value: (r) => rounded(r.avgDressedLbs, 2)},
    {header: 'Record ID', value: (r) => r.id || ''},
  ];
}

// Pig batches export whole-batch figures alongside the gilt/boar split, which
// is why every feed/head metric appears three times.
